import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Warehouse, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Warehouses() {
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/warehouses").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  const empName = (e) => e.nom_complet || `${e.prenom || ""} ${e.nom || ""}`.trim() || e.nom;
  const schema = [
    { name: "nom", label: "Nom du magasin", required: true },
    { name: "code", label: "Code magasin" },
    { name: "responsable_id", label: "Responsable (collaborateur)", type: "select", options: employees.map((e) => ({ value: e.id, label: empName(e) })) },
    { name: "telephone", label: "Téléphone" },
    { name: "localisation", label: "Adresse / localisation", col: 2 },
    { name: "statut", label: "Statut", type: "select", options: ["Actif", "Inactif"] },
    { name: "description", label: "Description", type: "textarea", col: 2 },
  ];

  const save = async (form) => {
    try {
      if (editing) await api.put(`/warehouses/${editing.id}`, form);
      else await api.post("/warehouses", form);
      toast.success(editing ? "Magasin mis à jour" : "Magasin créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce magasin ?")) return; await api.delete(`/warehouses/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Magasins" subtitle="Points de stockage & responsables">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-warehouse" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau magasin</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Warehouse} title="Aucun magasin" subtitle="Créez un magasin pour suivre le stock par emplacement." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Code</TableHead><TableHead>Nom</TableHead><TableHead>Responsable</TableHead><TableHead>Localisation</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((w) => (
                <TableRow key={w.id} data-testid={`warehouse-row-${w.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-500">{w.code || "—"}</TableCell>
                  <TableCell className="font-semibold">{w.nom}</TableCell>
                  <TableCell className="text-slate-600">{w.responsable || "—"}</TableCell>
                  <TableCell className="text-slate-600">{w.localisation || "—"}</TableCell>
                  <TableCell className="text-slate-600">{w.statut || "Actif"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`warehouse-menu-${w.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(w); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(w.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le magasin" : "Nouveau magasin"} schema={schema} initial={editing || { statut: "Actif" }} onSubmit={save} />
    </div>
  );
}
